<template>
  <v-btn
    icon
    small
    class="dialog-close"
    :aria-label="$t('close')"
    :title="$t('close')"
    @click="$emit('close')"
  >
    <v-icon small>mdi-close</v-icon>
  </v-btn>
</template>

<script>
/*
 * The way out of a dialog.
 *
 * Every dialog in the panel had two ways to leave it - a Cancel button at the
 * bottom, and clicking the grey outside - and neither is the one people reach
 * for. They press Escape, or they look for a cross in the corner, and this
 * had neither.
 *
 * Escape looks like it should already work: Vuetify closes a dialog on it.
 * But it binds that listener to the dialog's own content element, so the key
 * has to be pressed while something inside has focus - press it having
 * clicked the grey, or having not clicked anything at all, and nothing
 * happens. And a dialog marked persistent ignores the key entirely and
 * wobbles at you instead. So the listener here is on the document, where a
 * keystroke actually lands.
 *
 * Only the dialog on top answers. Vuetify stacks dialogs by writing a
 * z-index on each one's content, so "on top" is the largest of those among
 * the ones currently open - which is how a confirm raised from inside a form
 * closes itself and leaves the form standing.
 */
const listening = []

const contentOf = instance => {
  const el = instance.$el

  return el && el.closest ? el.closest(".v-dialog__content") : null
}

//a dialog's content stays in the page after it is dismissed, so being mounted
//says nothing about being open - the class Vuetify puts on it does
const isShowing = instance => {
  const el = instance.$el
  const dialog = el && el.closest ? el.closest(".v-dialog") : null

  return !!dialog && dialog.classList.contains("v-dialog--active")
}

const depth = instance => {
  const content = contentOf(instance)

  return content ? parseInt(content.style.zIndex, 10) || 0 : 0
}

const onKey = event => {
  if (event.key !== "Escape" && event.key !== "Esc" && event.keyCode !== 27) {
    return
  }

  const showing = listening.filter(isShowing)
  if (!showing.length) {
    return
  }

  let topmost = showing[showing.length - 1]
  showing.forEach(candidate => {
    if (depth(candidate) > depth(topmost)) {
      topmost = candidate
    }
  })

  topmost.$emit("close")
}

export default {
  name: "DialogClose",
  mounted() {
    if (!listening.length) {
      document.addEventListener("keydown", onKey)
    }
    listening.push(this)
  },
  beforeDestroy() {
    const at = listening.indexOf(this)
    if (at !== -1) {
      listening.splice(at, 1)
    }

    if (!listening.length) {
      document.removeEventListener("keydown", onKey)
    }
  },
}
</script>

<style>
/*
 * Not scoped: the padding belongs to the title beside it, so that a heading
 * long enough to reach the corner does not run underneath the cross.
 *
 * Placed with the end of the card rather than the right of it, because half
 * these schools read right to left and the corner moves with them.
 */
.dialog-close {
  position: absolute;
  top: 8px;
  inset-inline-end: 8px;
  z-index: 1;
}

.dialog-close ~ .v-card__title,
.dialog-close ~ * > .v-card__title {
  padding-inline-end: 48px;
}
</style>
